import { validateDatesArray, createLegacyDateString } from './newDateUtils';

/**
 * Unified date storage functions for the new date system
 * Handles storage and retrieval of unified date arrays
 */

// Store unified dates in document
export const storeUnifiedDates = (document, dates) => {
  if (!validateDatesArray(dates)) {
    console.error('Invalid dates array provided to storeUnifiedDates');
    return document;
  }

  // Create a clean document without undefined values
  const cleanDocument = { ...document };

  // Remove legacy date fields when using new system (don't set to undefined)
  const fieldsToRemove = [
    'leadTime',
    'serviceDate',
    'serviceStartDate',
    'serviceEndDate',
    'rentalStartDate',
    'rentalEndDate',
    'leadTimeMonths',
    'rentalDuration',
    'rentalDurationUnit',
    'leadTimeInputType',
    'serviceInputType',
    'rentalInputType',
    'dateInputType'
  ];

  fieldsToRemove.forEach(field => {
    if (cleanDocument.hasOwnProperty(field)) {
      delete cleanDocument[field];
    }
  });

  return {
    ...cleanDocument,
    dates: dates
  };
};

// Retrieve unified dates from document
export const retrieveUnifiedDates = (document) => {
  if (!document) return [];

  // If document has new unified dates, return them
  if (document.dates && Array.isArray(document.dates) && document.dates.length > 0) {
    return document.dates;
  }

  // If document has legacy dates, convert to legacy string for display
  if (hasLegacyDates(document)) {
    // Return empty array - legacy dates will be shown as string
    return [];
  }

  // New document - return empty array
  return [];
};

// Check if document has legacy date fields
export const hasLegacyDates = (document) => {
  if (!document) return false;

  return !!(
    document.leadTime ||
    document.serviceDate ||
    document.serviceStartDate ||
    document.serviceEndDate ||
    document.rentalStartDate ||
    document.rentalEndDate ||
    document.leadTimeMonths ||
    document.rentalDuration ||
    document.rentalDurationUnit
  );
};

// Get legacy date information as readable string
export const getLegacyDateInfo = (document) => {
  if (!hasLegacyDates(document)) return null;
  return createLegacyDateString(document);
};

// Check if document uses new unified date system
export const usesUnifiedDates = (document) => {
  return document &&
         document.dates &&
         Array.isArray(document.dates) &&
         document.dates.length > 0;
};

// Migrate document to unified date system (preserves legacy info)
export const migrateToUnifiedDates = (document, newDates = []) => {
  if (!validateDatesArray(newDates)) {
    console.error('Invalid dates array provided to migrateToUnifiedDates');
    return document;
  }

  const legacyInfo = getLegacyDateInfo(document);

  // Create a clean document without undefined values
  const cleanDocument = { ...document };

  // Remove legacy date fields when migrating (don't set to undefined)
  const fieldsToRemove = [
    'leadTime',
    'serviceDate',
    'serviceStartDate',
    'serviceEndDate',
    'rentalStartDate',
    'rentalEndDate',
    'leadTimeMonths',
    'rentalDuration',
    'rentalDurationUnit',
    'leadTimeInputType',
    'serviceInputType',
    'rentalInputType',
    'dateInputType'
  ];

  fieldsToRemove.forEach(field => {
    if (cleanDocument.hasOwnProperty(field)) {
      delete cleanDocument[field];
    }
  });

  return {
    ...cleanDocument,
    dates: newDates,
    legacyDateInfo: legacyInfo
  };
};

// Get dates for PDF generation (handles both systems)
export const getDatesForPDF = (document) => {
  if (!document) return '';

  // If using unified dates, format them
  if (usesUnifiedDates(document)) {
    const { formatMultipleDatesForPDF } = require('./newDateUtils');
    return formatMultipleDatesForPDF(document.dates);
  }

  // If has legacy dates, return legacy string
  if (hasLegacyDates(document)) {
    return getLegacyDateInfo(document);
  }

  // No dates
  return '';
};

// Get appropriate date label for PDF (Date vs Dates)
export const getDateLabelForPDF = (document) => {
  if (!document) return 'Date';

  // If using unified dates, check array length
  if (usesUnifiedDates(document)) {
    return document.dates && document.dates.length > 1 ? 'Dates' : 'Date';
  }

  // For legacy dates, always use singular since legacy system doesn't support multiple dates
  return 'Date';
};

// Validate document dates before saving
export const validateDocumentDates = (document) => {
  if (!document) return { isValid: false, error: 'Document is null' };

  // If using unified dates, validate them
  if (usesUnifiedDates(document)) {
    if (!validateDatesArray(document.dates)) {
      return { isValid: false, error: 'Invalid unified dates array' };
    }
  }

  // If has legacy dates but no unified dates, that's also valid
  // (legacy dates are preserved as-is)

  return { isValid: true };
};

// Create backup of legacy dates before migration
export const createLegacyBackup = (document) => {
  if (!hasLegacyDates(document)) return null;

  return {
    leadTime: document.leadTime,
    serviceDate: document.serviceDate,
    serviceStartDate: document.serviceStartDate,
    serviceEndDate: document.serviceEndDate,
    rentalStartDate: document.rentalStartDate,
    rentalEndDate: document.rentalEndDate,
    leadTimeMonths: document.leadTimeMonths,
    rentalDuration: document.rentalDuration,
    rentalDurationUnit: document.rentalDurationUnit,
    leadTimeInputType: document.leadTimeInputType,
    serviceInputType: document.serviceInputType,
    rentalInputType: document.rentalInputType,
    dateInputType: document.dateInputType,
    legacyDateString: getLegacyDateInfo(document)
  };
};

// Restore from legacy backup (for rollback)
export const restoreFromLegacyBackup = (document, backup) => {
  if (!backup) return document;

  // Create a clean document without undefined values
  const cleanDocument = { ...document };

  // Remove unified dates and legacy info when restoring from backup
  if (cleanDocument.hasOwnProperty('dates')) {
    delete cleanDocument.dates;
  }
  if (cleanDocument.hasOwnProperty('legacyDateInfo')) {
    delete cleanDocument.legacyDateInfo;
  }

  return {
    ...cleanDocument,
    ...backup,
    dates: [], // Clear unified dates
    legacyDateInfo: null // Clear legacy info string (use null instead of undefined)
  };
};

// Debug document date state
export const debugDocumentDates = (document, label = 'Document') => {
  console.log(`${label} Date State:`, {
    hasUnifiedDates: usesUnifiedDates(document),
    hasLegacyDates: hasLegacyDates(document),
    legacyDateInfo: getLegacyDateInfo(document),
    datesCount: document.dates ? document.dates.length : 0,
    dates: document.dates
  });
};
